<script setup lang="ts">
/**
 * ElectionHub Component
 *
 * Main container for the election hub feature.
 * Displays election map, grid, and detail views with filtering controls.
 *
 * Data Sources:
 * - Dynamic tabs: /category/tree API via useElectionCategories
 * - Map coloring: /election/map API via useElectionMapData
 * - Grid data: Mock data (pending API integration)
 *
 * @see Plan: docs/plans/2026-01-27-feat-election-hub-react-to-vue-migration-plan.md
 * @see Plan: docs/plans/2026-01-28-feat-election-hub-data-integration-plan.md
 */
import { ref, computed, watch, onMounted, onUnmounted, onBeforeUnmount, provide, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useElementSize } from '@vueuse/core';
import { useUsDomain } from '@/composables/useUsDomain';

const { isUsDomain } = useUsDomain();
const SCROLL_CONTAINER_ID = 'RATIO_SCROLL_CONTAINER';
const { t } = useI18n();

import { useRoute, useRouter } from 'vue-router';
import ElectionMap from './ElectionMap.vue';
import ElectionGrid from './ElectionGrid.vue';
import ElectionDetailsPanel from './ElectionDetailsPanel.vue';
import ShareBetSlipDialog from '@/components/ShareBetSlipDialog.vue';
import BetSlipDrawer from '@/components/BetSlipDrawer.vue';
import OpenAccountBanner from '@/components/OpenAccountBanner.vue';
import { useElectionData, type SelectionInfo } from '../composables/useElectionData';
import { useElectionMap } from '../composables/useElectionMap';
import { useElectionCategories, type ElectionTab } from '../composables/useElectionCategories';
import { useElectionMapData, TOTAL_SEATS, type ConfidenceBand } from '../composables/useElectionMapData';
import { formatCompactNumber } from '@/utils/formatNumber';
import { GOVERNOR_CONTRACT_DATA, getAllHouseContracts, getAllSenateContracts } from '../data/mockElectionData';
import {
  ELECTION_COLORS,
  type ElectionContract,
  type ElectionType,
  type ViewMode,
  type USStateCode,
} from '../types/election.types';
import type {
  ForecastTraderElectionMapParams,
  ForecastTraderElectionMapProduct,
  ElectionOffice,
} from '@/types/forecasttrader.types';
import { app } from '@ibgroup-tws-webapps/ratio.ui';
import { useUserState } from '@/stores/useUserState';
import { onShareDialogOpenRequest } from '@/components/views/useShareDialogDeepLink';
import { normalizeDeepLinkQuery, stripDeepLinkParams, registerViewHandler } from '@/utils/deepLink';
import {
  ELECTION_BOARD_TAB_KEY,
  parseElectionBoardDetailsTarget,
  parseElectionBoardTab,
  resolveElectionBoardViewMode,
  stripElectionBoardDetailsParams,
  type ElectionBoardTab,
} from '../utils/predictionMarketsNavigation';

// Props interface
interface Props {
  /** Whether betslip should be visible */
  betslipVisible?: boolean;
  /** Betslip configuration */
  betslipConfig?: {
    conid?: number;
    productConid?: number;
    showShareDialog?: boolean;
  } | null;
  /** Whether share dialog should be visible (module path) */
  shareDialogVisible?: boolean;
  /** Share dialog configuration (module path) */
  shareDialogConfig?: {
    conid?: number;
    productConid?: number;
  } | null;
}

const props = withDefaults(defineProps<Props>(), {
  betslipVisible: false,
  betslipConfig: null,
  shareDialogVisible: false,
  shareDialogConfig: null,
});

const hubStickyRef = ref<HTMLElement | null>(null);

const hubStickyHeight = ref(0);
let hubResizeObserver: ResizeObserver | null = null;

provide('stickyOffset', hubStickyHeight);

/**
 * Map ElectionType to office param
 */
function electionTypeToOffice(type: ElectionType): ElectionOffice | null {
  switch (type) {
    case 'Governor':
      return 'governor';
    case 'House':
      return 'house';
    case 'Senate':
      return 'senate';
    default:
      return null;
  }
}

// Get contracts for current election type (legacy - for grid view)
function getContracts(type: ElectionType): ElectionContract[] {
  switch (type) {
    case 'House':
      return getAllHouseContracts();
    case 'Senate':
      return getAllSenateContracts();
    case 'All By State':
      return Object.entries(GOVERNOR_CONTRACT_DATA).map(([code, data]) => ({
        ...data,
        stateCode: code as USStateCode,
      }));
    default:
      return Object.entries(GOVERNOR_CONTRACT_DATA).map(([code, data]) => ({
        ...data,
        stateCode: code as USStateCode,
      }));
  }
}

// ===== API-Driven Composables =====
// Dynamic tabs from /category/tree API
const { electionTabs, isLoading: isTabsLoading } = useElectionCategories();

// Current tab state (stores category ID like 'g17467'/'g137914' or '__all__')
const selectedTabId = ref<string | null>(null);

const headerRowRef = ref<HTMLElement | null>(null);
const { width: headerRowWidth } = useElementSize(headerRowRef);
const isMobileCollapsed = computed(() => headerRowWidth.value > 0 && headerRowWidth.value < 768);
provide('hubTabsCollapsed', isMobileCollapsed);

// Category label for mobile compact button
const selectedCategoryLabel = computed(() => {
  const tab = electionTabs.value.find((t) => t.id === selectedTabId.value);
  return tab?.label ?? '\u2026';
});

const categoryPopoverRef = ref();
const toggleCategoryPopover = (event: Event) => categoryPopoverRef.value?.toggle(event);

// PrimeVue's scroll handler misses native viewport scrolling — close popover on scroll.
const hideCategoryPopover = () => categoryPopoverRef.value?.hide();
onMounted(() => window.addEventListener('scroll', hideCategoryPopover, { passive: true }));
onUnmounted(() => window.removeEventListener('scroll', hideCategoryPopover));

// Ref for the scrollable tab container — used to scroll active tab into view
const tabContainerRef = ref<HTMLElement | null>(null);

/**
 * Scroll the currently-active PrimeVue Tab into view inside the scrollable TabList.
 * PrimeVue's scrollable TabList uses overflow:hidden on .p-tablist-content and manages
 * scroll position programmatically, so scrollIntoView() won't work — we set scrollLeft directly.
 */
function scrollActiveTabIntoView() {
  nextTick(() => {
    const container = tabContainerRef.value;
    if (!container) return;
    const scrollEl = container.querySelector<HTMLElement>('.p-tablist-content');
    const activeTab = container.querySelector<HTMLElement>('[data-p-active="true"]');
    if (!scrollEl || !activeTab) return;

    // Center the active tab within PrimeVue's scroll container
    const scrollLeft = activeTab.offsetLeft - scrollEl.offsetWidth / 2 + activeTab.offsetWidth / 2;
    scrollEl.scrollTo({ left: Math.max(0, scrollLeft), behavior: 'smooth' });
  });
}

const gridRaceTypeParams = { raceType: 'general' as const };

// Computed: is "All By State" tab selected?
const isAllByState = computed(() => currentTab.value === 'All By State');

// Track tab switching for transition disabling
const isTabSwitching = ref(false);

// Map selected tab ID to ElectionType for legacy components
const currentTab = computed<ElectionType>(() => {
  if (!selectedTabId.value) return 'Governor';
  const tab = electionTabs.value.find((t) => t.id === selectedTabId.value);
  if (!tab) return 'Governor';
  // Map label back to ElectionType
  if (tab.label === 'Governor') return 'Governor';
  if (tab.label === 'House') return 'House';
  if (tab.label === 'Senate') return 'Senate';
  if (tab.label === 'All By State') return 'All By State';
  return 'Governor';
});

// Carried on Prediction Markets links so "Return to Election Board" restores this tab
provide(
  ELECTION_BOARD_TAB_KEY,
  computed<ElectionBoardTab>(() =>
    currentTab.value === 'All By State' ? 'by-state' : (currentTab.value.toLowerCase() as ElectionBoardTab),
  ),
);

/** Resolve an eb_tab query value (Prediction Markets return link) to a loaded tab */
function findTabForBoardTab(tabs: ElectionTab[], boardTab: ElectionBoardTab): ElectionTab | undefined {
  if (boardTab === 'by-state') return tabs.find((t) => t.id === '__all__');
  return tabs.find((t) => t.label.toLowerCase() === boardTab);
}

// Vue Router for route-driven view mode and deep linking
const route = useRoute();
const router = useRouter();
const userState = useUserState();

// 'dashboard' shows map + summary, 'grid' shows full election grid (no map)
// Driven by route.meta.viewMode set in electionHubRoutes.ts
// Declared early because electionMapParams (below) depends on it.
const currentViewMode = ref<'dashboard' | 'grid'>(
  resolveElectionBoardViewMode(route.query, route.meta.viewMode as 'dashboard' | 'grid' | undefined),
);

// Build general-election map params from the current tab
// Only fetch map data on the dashboard view (map is not shown on grid view)
const electionMapParams = computed<ForecastTraderElectionMapParams | null>(() => {
  if (currentViewMode.value !== 'dashboard') return null;
  const office = electionTypeToOffice(currentTab.value);
  if (!office) return null;

  return {
    office,
    raceType: 'general',
  };
});

// Map data from /election/map API
const {
  stateFills,
  stateData,
  partyAggregates,
  confidenceBands,
  openInterest: categoryOpenInterest,
  isLoading: isMapLoading,
} = useElectionMapData(electionMapParams);

// Show skeleton for map aggregates when loading or when we don't have data yet
const showMapSkeleton = computed(() => {
  return isMapLoading.value || partyAggregates.value.total === 0;
});

// Total seats for current tab (used as progress bar denominator)
const totalSeatsForCurrentTab = computed(() => {
  const office = electionTypeToOffice(currentTab.value);
  return office ? (TOTAL_SEATS[office] ?? 0) : 0;
});

// Seats not yet available from API
const notAvailableSeats = computed(() => {
  return totalSeatsForCurrentTab.value - partyAggregates.value.total;
});

// Only Senate and Governor show "N not active" label
const showNotAvailable = computed(() => {
  return currentTab.value === 'Senate' || currentTab.value === 'Governor';
});

// Map a confidence band to its CSS custom property color
function getBandColor(band: ConfidenceBand): string {
  if (band.party === 'Other') return 'var(--color-map-tie)';
  const partyKey = band.party === 'Democrat' ? 'democrat' : 'republican';
  const cents = [50, 60, 70, 80][band.intensity] ?? 80;
  return `var(--color-${partyKey}-map-${cents})`;
}

// Hover state for confidence bands
const hoveredBandIndex = ref<number | null>(null);
const progressBarEl = ref<HTMLElement | null>(null);

// Track touch vs mouse input to avoid conflicting interactions
const isTouchDevice = ref(false);
function onBandTouchStart() {
  isTouchDevice.value = true;
}
function onBandMouseEnter(idx: number) {
  if (!isTouchDevice.value) hoveredBandIndex.value = idx;
}
function onBandMouseLeave() {
  if (!isTouchDevice.value) hoveredBandIndex.value = null;
}
function onBandClick(idx: number) {
  if (isTouchDevice.value) {
    hoveredBandIndex.value = hoveredBandIndex.value === idx ? null : idx;
  }
}

// Dismiss band selection when tapping outside the bar on touch devices
function onDocumentClick(e: MouseEvent) {
  if (isTouchDevice.value && hoveredBandIndex.value != null && progressBarEl.value) {
    if (!progressBarEl.value.contains(e.target as Node)) {
      hoveredBandIndex.value = null;
    }
  }
}
onMounted(() => document.addEventListener('click', onDocumentClick));
onUnmounted(() => document.removeEventListener('click', onDocumentClick));

// In native webviews the scroll container (RATIO_SCROLL_CONTAINER) is the only
// scrollable element (parent is h-screen overflow-hidden). Overriding its
// overflow to "visible" removes the scrollable surface and breaks touch scroll.
// Only override in browser contexts where the native viewport handles scroll.
const isNativePlatform = app.option.clientPlatform !== 'web' && app.option.platform !== 'web';
let savedContainerOverflow = '';
let savedParentOverflow = '';
onMounted(() => {
  if (isNativePlatform) return;
  const el = document.getElementById(SCROLL_CONTAINER_ID);
  if (el) {
    savedContainerOverflow = el.style.overflow;
    el.style.overflow = 'visible';
    if (el.parentElement) {
      savedParentOverflow = el.parentElement.style.overflow;
      el.parentElement.style.overflow = 'visible';
    }
  }
});
onBeforeUnmount(() => {
  if (isNativePlatform) return;
  const el = document.getElementById(SCROLL_CONTAINER_ID);
  if (el) {
    el.style.overflow = savedContainerOverflow;
    if (el.parentElement) {
      el.parentElement.style.overflow = savedParentOverflow;
    }
  }
  hubResizeObserver?.disconnect();
});

onMounted(() => {
  if (hubStickyRef.value) {
    hubResizeObserver = new ResizeObserver((entries) => {
      hubStickyHeight.value = entries[0].contentRect.height;
    });
    hubResizeObserver.observe(hubStickyRef.value);
  }
});

// Description label for a hovered confidence band
function getBandDescription(band: ConfidenceBand): string {
  if (band.party === 'Other') return 'Toss-up races';
  const partyLabel = band.party;
  switch (band.intensity) {
    case 0:
      return `${partyLabel} win probability less than 60%`;
    case 1:
      return `${partyLabel} win probability greater than 60% less than 70%`;
    case 2:
      return `${partyLabel} win probability greater than 70% less than 80%`;
    case 3:
      return `${partyLabel} win probability greater than 80%`;
    default:
      return `${partyLabel} win probability`;
  }
}

const showOpenInterestSkeleton = computed(() => isMapLoading.value);

// Register by-state deep link handler so the deep link system doesn't warn.
// ElectionHub reads ?view=by-state directly from the route — the handler just
// needs to exist so processDeepLink treats it as known.
const cleanupByStateHandler = registerViewHandler('by-state', () => {
  const allTab = electionTabs.value.find((t) => t.id === '__all__');
  if (allTab) {
    selectedTabId.value = allTab.id;
    electionType.value = 'All By State';
    scrollActiveTabIntoView();
  }
});

// Auto-select first tab when tabs load (or sync from URL)
watch(
  electionTabs,
  (tabs) => {
    if (tabs.length > 0 && !selectedTabId.value) {
      // Returning from Prediction Markets: restore the tab the user left from
      const boardTab = parseElectionBoardTab(route.query);
      const returnTab = boardTab ? findTabForBoardTab(tabs, boardTab) : undefined;
      if (returnTab) {
        selectedTabId.value = returnTab.id;
        scrollActiveTabIntoView();
        return;
      }
      // If URL has ?view=by-state, select "All By State" tab
      if (route.query.view === 'by-state') {
        const allTab = tabs.find((t) => t.id === '__all__');
        if (allTab) {
          selectedTabId.value = allTab.id;
          electionType.value = 'All By State';
          scrollActiveTabIntoView();
          return;
        }
      }
      selectedTabId.value = tabs[0].id;
    }
  },
  { immediate: true },
);

// Keep the active tab scrolled into view whenever the selected tab changes
// or the tab bar becomes visible (e.g. viewport resize from collapsed → expanded).
// Covers programmatic tab switches (like map-view redirect from "All By State" → Governor)
// that don't go through handleTabChange().
watch([selectedTabId, isMobileCollapsed], () => {
  if (!isMobileCollapsed.value) {
    scrollActiveTabIntoView();
  }
});

// Keep currentViewMode in sync with route changes (e.g. browser back/forward, direct navigation)
watch([() => route.meta.viewMode, () => route.query.eb_view], ([mode]) => {
  currentViewMode.value = resolveElectionBoardViewMode(route.query, mode as 'dashboard' | 'grid' | undefined);
});
// Within grid view, toggle between card and table layouts
const gridLayoutMode = ref<ViewMode>('table');
const contracts = computed(() => getContracts(currentTab.value));

// Use election data composable
const { electionType, filteredContracts: baseFilteredContracts } = useElectionData(contracts);

// Apply additional grid filters (for deep linking)
// NOTE: filteredContracts currently unused as ElectionGrid is now API-driven
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const filteredContracts = computed(() => {
  let result = baseFilteredContracts.value;

  // Apply product filter if specified
  if (gridFilters.value.productConid) {
    result = result.filter((contract) => contract.productConid === gridFilters.value.productConid);
  }

  return result;
});

// Clear grid filters when switching to grid view via route change
watch(currentViewMode, (newMode) => {
  if (newMode === 'grid') {
    gridFilters.value = {};
  }
});

// Map composable
const { loadMap } = useElectionMap();

// Handle view mode toggle via router navigation
function handleViewModeChange(mode: 'dashboard' | 'grid') {
  window.scrollTo(0, 0);
  // "All By State" has no map view \u2014 switch to Governor when going to map
  const query = { ...route.query };
  if (mode === 'dashboard' && isAllByState.value) {
    const govTab = electionTabs.value.find((t) => t.label === 'Governor');
    if (govTab) selectedTabId.value = govTab.id;
    delete query.view;
  }
  currentViewMode.value = mode;
  const path = mode === 'grid' ? '/election-hub/grid' : '/election-hub/map';
  router.push({ path, query });
}

// Grid filtering state (for deep link filtering)
const gridFilters = ref<{ productConid?: number }>({});

// Map zoom state (driven by ElectionMap emit)
const isMapZoomed = ref(false);

// Details drawer state
const detailsDrawerVisible = ref(false);
const selectedProducts = ref<ForecastTraderElectionMapProduct[]>([]);
const initialProductConid = ref<number | undefined>(undefined);
const initialCandidateConid = ref<number | undefined>(undefined);
const selectedParty = ref<string | undefined>(undefined);
const selectedColor = ref<number | undefined>(undefined);

// Share modal state
const shareModalVisible = ref(false);
const shareCandidateConid = ref<number>();
const shareProductConid = ref<number>(); // For product-level deep link share

// BetSlip drawer state
const betSlipVisible = ref(false);
const betSlipConid = ref<number | undefined>(undefined);
const betSlipProductConid = ref<number | undefined>(undefined);

// Watch props to control betslip visibility (module path)
watch(
  () => props.betslipVisible,
  (visible) => {
    if (visible) {
      betSlipConid.value = props.betslipConfig?.conid;
      betSlipProductConid.value = props.betslipConfig?.productConid;
      betSlipVisible.value = true;

      // Also open share dialog on top when showShareDialog is set
      if (props.betslipConfig?.showShareDialog) {
        const conid = props.betslipConfig.conid;
        const productConid = props.betslipConfig.productConid;
        if (conid) {
          shareCandidateConid.value = conid;
        } else if (productConid) {
          shareProductConid.value = productConid;
        }
        nextTick(() => {
          shareModalVisible.value = true;
        });
      }
    }
  },
  { immediate: true },
);

// Watch props to control share dialog visibility (module path)
watch(
  () => props.shareDialogVisible,
  (visible) => {
    if (visible) {
      const conid = props.shareDialogConfig?.conid;
      const productConid = props.shareDialogConfig?.productConid;
      if (conid) {
        shareCandidateConid.value = conid;
      } else if (productConid) {
        shareProductConid.value = productConid;
      }
      shareModalVisible.value = true;
    }
  },
  { immediate: true },
);

// Track consumed deep link fingerprint to avoid re-triggering on
// internal navigations (Map/List toggle, View Election Board, etc.)
// while still allowing new betslip opens with different params.
const lastConsumedDeepLink = ref<string | null>(null);

// Watch route query for deep links (standalone + module hash router)
watch(
  () => route.query,
  (query) => {
    // Normalize eh_* prefixed params from marketing emails
    const rawParams: Record<string, string> = {};
    Object.entries(query).forEach(([key, value]) => {
      rawParams[key] = Array.isArray(value) ? (value[0] ?? '') : ((value as string) ?? '');
    });
    const normalized = normalizeDeepLinkQuery(rawParams);

    if (!normalized.view) return;

    // Build a fingerprint of the deep link params to detect duplicates
    const fingerprint = `${normalized.view}:${normalized.conid || ''}:${normalized.productConid || ''}:${normalized.showShareDialog || ''}`;
    if (fingerprint === lastConsumedDeepLink.value) return;

    if (normalized.view === 'sharecontracts') {
      const conid = normalized.conid ? Number(normalized.conid) : undefined;
      const productConid = normalized.productConid ? Number(normalized.productConid) : undefined;
      if ((conid && conid > 0) || (productConid && productConid > 0)) {
        betSlipConid.value = conid;
        betSlipProductConid.value = productConid;
        betSlipVisible.value = true;
        lastConsumedDeepLink.value = fingerprint;

        // Also open share dialog on top when show_share_dialog=1
        // Use nextTick to ensure dialog component is mounted before setting visible
        if (normalized.showShareDialog === '1') {
          if (conid) {
            shareCandidateConid.value = conid;
          } else if (productConid) {
            shareProductConid.value = productConid;
          }
          nextTick(() => {
            shareModalVisible.value = true;
          });
        }
      }
    }

    if (normalized.view === 'sharedialog') {
      const conid = normalized.conid ? Number(normalized.conid) : undefined;
      const productConid = normalized.productConid ? Number(normalized.productConid) : undefined;
      if (conid) {
        shareCandidateConid.value = conid;
      } else if (productConid) {
        shareProductConid.value = productConid;
      }
      shareModalVisible.value = true;
      lastConsumedDeepLink.value = fingerprint;
    }
  },
  { immediate: true },
);

// Share dialog deep link event listener
let cleanupShareDialogListener: (() => void) | null = null;

// Bridge listener for smart back navigation. Must be wired inside a child of
// <ib-application> — not in ElectionHubModule — so the bridge exists by the
// time we subscribe.
let unsubscribeBridge: (() => void) | undefined;

// Load map data and initialize native platform features on mount
onMounted(() => {
  loadMap();

  // Set native header for mobile platforms
  if (app.option.clientPlatform !== 'web' && app.option.platform !== 'web') {
    app.send({
      action: 'native_header',
      data: {
        title: 'Election Board',
      },
    });
  }

  // Listen for share dialog open requests from deep link system
  cleanupShareDialogListener = onShareDialogOpenRequest((detail) => {
    if (detail.conid) {
      shareCandidateConid.value = detail.conid;
    } else if (detail.productConid) {
      shareProductConid.value = detail.productConid;
    }
    shareModalVisible.value = true;
  });

  // Register bridge listener for smart back navigation on native platforms
  const bridge = app.getBridge();
  unsubscribeBridge = bridge?.onMessage((msg) => {
    if (msg.action === 'back') {
      if (detailsDrawerVisible.value) {
        detailsDrawerVisible.value = false;
        app.send({ action: 'back', data: { exit: false } });
        return;
      }
      if (currentViewMode.value === 'dashboard') {
        router.replace({ name: 'ElectionGrid', query: route.query });
        app.send({ action: 'back', data: { exit: false } });
      } else {
        app.send({ action: 'back', data: { exit: true } });
      }
      return;
    }
  });
});

onBeforeUnmount(() => {
  cleanupByStateHandler();
  cleanupShareDialogListener?.();
  unsubscribeBridge?.();
});

// Update election type when tab changes (now receives tab ID from API)
function handleTabChange(tabId: string) {
  // Set tab switching flag to disable transitions temporarily
  isTabSwitching.value = true;
  selectedTabId.value = tabId;
  // Also update legacy electionType ref for grid filtering
  electionType.value = currentTab.value;
  // Re-enable transitions after Vue has processed the update
  requestAnimationFrame(() => {
    isTabSwitching.value = false;
  });
  // Scroll the selected tab into view (e.g. when last tab is off-screen on iPad)
  scrollActiveTabIntoView();

  // "All By State" auto-navigates to grid view with query param
  // A manual tab switch supersedes the Prediction Markets return tab

  const { eb_tab: _ebTab, ...queryWithoutReturnTab } = route.query;
  if (tabId === '__all__') {
    gridLayoutMode.value = 'table';
    const query = { ...queryWithoutReturnTab, view: 'by-state' };
    router.push({ path: '/election-hub/grid', query });
  } else if (route.query.view === 'by-state') {
    // Switching away from "All By State" — remove the query param
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { view: _view, ...rest } = queryWithoutReturnTab;
    router.replace({ query: rest });
  } else if (_ebTab !== undefined) {
    router.replace({ query: queryWithoutReturnTab });
  }
}

// Handle state selection from map
function handleMapSelectState(stateCode: USStateCode) {
  const contract = contracts.value.find((c) => c.stateCode === stateCode);
  if (contract) {
    // Get party/color from map state data
    const stateInfo = stateData.value.get(stateCode);
    openDetailsDrawer({
      stateCode,
      type: currentTab.value === 'All By State' ? 'Governor' : currentTab.value,
      party: stateInfo?.party || undefined,
      color: stateInfo?.intensity,
    });
  }
}

// Handle selection from grid (race-level)
function handleGridSelectState(info: SelectionInfo) {
  initialProductConid.value = info.productConid;
  initialCandidateConid.value = undefined;
  openDetailsDrawer(info);
}

// Handle candidate selection from grid (candidate-level)
function handleGridSelectCandidate(info: SelectionInfo) {
  initialProductConid.value = info.productConid;
  initialCandidateConid.value = info.candidateConid;
  openDetailsDrawer(info);
}

// Open details drawer
function openDetailsDrawer(info: SelectionInfo) {
  // Get products from the election map data
  const stateInfo = stateData.value.get(info.stateCode);
  if (stateInfo && stateInfo.products.length > 0) {
    selectedProducts.value = [...stateInfo.products];
  } else {
    // Use the productConid from the grid selection (e.g. "All By State" mode where map data is unavailable)
    selectedProducts.value = [{ conid: info.productConid!, name: `${info.stateCode} Election` }];
  }
  selectedParty.value = info.party;
  selectedColor.value = info.color;
  detailsDrawerVisible.value = true;
}

// Handle share from details drawer.
// When a candidate conid is provided (per-candidate share button), the share dialog
// opens scoped to that candidate; otherwise it falls back to product-level share.
function handleShare(candidateConid?: number) {
  if (candidateConid) {
    shareCandidateConid.value = candidateConid;
  }
  shareModalVisible.value = true;
}

// Handle share from grid/table row
function handleGridShare(candidateConid: number) {
  shareCandidateConid.value = candidateConid;
  shareModalVisible.value = true;
}

// Handle watchlist toggle
function handleToggleWatchlist() {
  // The ElectionDetailsPanel now handles watchlist toggle internally
  // This is kept for compatibility with the emit
  console.log('Watchlist toggle requested');
}

// Clear initial props when dialog closes
watch(detailsDrawerVisible, (visible) => {
  if (!visible) {
    initialProductConid.value = undefined;
    initialCandidateConid.value = undefined;
    selectedParty.value = undefined;
    selectedColor.value = undefined;
    // Drop the modal deep link so late map data or a reload doesn't reopen it
    activeReturnDeepLink.value = null;
    const cleanQuery = stripElectionBoardDetailsParams(route.query);
    if (cleanQuery) router.replace({ query: cleanQuery });
  }
});

// When betslip or share dialog closes, strip deep link params from the URL
// so navigating around doesn't re-trigger them.
watch(betSlipVisible, (visible) => {
  if (!visible) {
    lastConsumedDeepLink.value = null;
    const cleanQuery = stripDeepLinkParams(route.query);
    if (cleanQuery) router.replace({ query: cleanQuery });
  }
});
watch(shareModalVisible, (visible) => {
  if (!visible) {
    shareCandidateConid.value = undefined;
    shareProductConid.value = undefined;
    lastConsumedDeepLink.value = null;
    const cleanQuery = stripDeepLinkParams(route.query);
    if (cleanQuery) router.replace({ query: cleanQuery });
  }
});

const formatOI = formatCompactNumber;

// Legend swatches: DEM high→low, Tie, REP low→high
const LEGEND_SWATCHES = [
  { label: '80¢', color: '--color-democrat-map-80' },
  { label: '70¢', color: '--color-democrat-map-70' },
  { label: '60¢', color: '--color-democrat-map-60' },
  { label: '50¢', color: '--color-democrat-map-50' },
  { label: 'Tie', color: '--color-map-tie' },
  { label: '50¢', color: '--color-republican-map-50' },
  { label: '60¢', color: '--color-republican-map-60' },
  { label: '70¢', color: '--color-republican-map-70' },
  { label: '80¢', color: '--color-republican-map-80' },
];

// Handle showGrid event from ElectionDetailsPanel
function handleShowGrid(options?: { filters?: string }) {
  // Close the details drawer
  detailsDrawerVisible.value = false;

  // If filters are provided, apply them
  if (options?.filters) {
    const filterPairs = options.filters.split(',');
    const newFilters: { productConid?: number } = {};

    for (const pair of filterPairs) {
      const [key, value] = pair.split(':');
      if (key === 'product' && value) {
        const productConid = parseInt(value, 10);
        if (!isNaN(productConid)) {
          newFilters.productConid = productConid;
        }
      }
    }

    gridFilters.value = newFilters;
  } else {
    gridFilters.value = {};
  }

  // Navigate to grid view via router
  router.push({ path: '/election-hub/grid', query: route.query });
}

// --- Deep link query param processing ---

function openDetailsFromDeepLink(productConid: number, candidateConid?: number) {
  // Search stateData for the product
  for (const [, stateInfo] of stateData.value) {
    const product = stateInfo.products.find((p) => p.conid === productConid);
    if (product) {
      selectedProducts.value = [...stateInfo.products];
      initialProductConid.value = productConid;
      initialCandidateConid.value = candidateConid;
      detailsDrawerVisible.value = true;
      return;
    }
  }

  // Product not found in stateData — create temporary product (panel will fetch its own data)
  selectedProducts.value = [{ conid: productConid, name: '' }];
  initialProductConid.value = productConid;
  initialCandidateConid.value = candidateConid;
  detailsDrawerVisible.value = true;
}

// Keep the active return target so map data can enrich the temporary product
// after the modal has already opened. Grid view never requests map data, so a
// return deep link must not wait for stateData before opening.
const activeReturnDeepLink = ref<{ productConid: number; candidateConid?: number } | null>(null);

// Watch route.query for deep link params
watch(
  () => route.query,
  (query) => {
    const target = parseElectionBoardDetailsTarget(query);
    if (target) {
      activeReturnDeepLink.value = target;
      openDetailsFromDeepLink(target.productConid, target.candidateConid);
    } else {
      activeReturnDeepLink.value = null;
    }
  },
  { immediate: true },
);

// If map data arrives later, replace the temporary one-product context with
// the complete state product set without delaying the initial modal.
watch(stateData, (data) => {
  if (data.size > 0 && activeReturnDeepLink.value) {
    const { productConid, candidateConid } = activeReturnDeepLink.value;
    openDetailsFromDeepLink(productConid, candidateConid);
  }
});
</script>

<template>
  <div class="text-on-surface font-sans bg-surface">
    <!-- Sticky header: tabs + compact controls -->
    <div
      ref="hubStickyRef"
      class="hub-sticky-header sticky z-20 border-b border-transparent shadow-[0_2px_8px_rgb(0_0_0/6%)]"
    >
      <!-- Secondary Navigation (Election Type Tabs) -->
      <div class="hub-sticky-header__tabs bg-surface border-b border-[--p-content-border-color]">
        <div ref="headerRowRef" class="mx-auto max-w-[1600px] px-3 sm:px-8 lg:px-12">
          <div class="flex items-center gap-2" :class="isMobileCollapsed ? 'justify-start' : 'justify-between'">
            <!-- Full tab bar: hidden when container is too narrow -->
            <div v-if="!isMobileCollapsed" ref="tabContainerRef" class="min-w-0 overflow-x-auto scrollbar-hide">
              <div v-if="isTabsLoading" class="flex gap-10 py-4">
                <Skeleton width="5rem" height="10px" />
                <Skeleton width="4rem" height="10px" />
                <Skeleton width="4.5rem" height="10px" />
                <Skeleton width="6rem" height="10px" />
              </div>
              <Tabs v-else :value="selectedTabId ?? ''" @update:value="handleTabChange($event as string)" scrollable>
                <TabList
                  :pt="{
                    root: 'bg-transparent !border-0',
                    tabList: 'bg-transparent !border-0',
                    activeBar: { class: 'hub-active-bar', style: { background: ELECTION_COLORS.TITLE_GOLD } },
                  }"
                >
                  <Tab
                    v-for="tab in electionTabs"
                    :key="tab.id"
                    :value="tab.id"
                    :pt="{
                      root: {
                        class: [
                          'bg-transparent text-xs md:text-sm lg:!text-base !font-black uppercase tracking-widest transition-colors !border-0 shadow-none rounded-none !px-2 md:!px-3',
                          selectedTabId !== tab.id && '!text-on-surface',
                        ],
                        style: selectedTabId === tab.id ? { color: ELECTION_COLORS.TITLE_GOLD } : undefined,
                      },
                    }"
                  >
                    {{ tab.label }}
                  </Tab>
                </TabList>
              </Tabs>
            </div>

            <!-- Mobile collapsed category selector -->
            <div v-if="isMobileCollapsed" class="flex shrink-0 items-center py-1.5">
              <Skeleton v-if="isTabsLoading" width="7rem" height="2.25rem" borderRadius="8px" />
              <template v-else>
                <button
                  class="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-[--p-content-border-color] bg-surface text-on-surface text-sm font-extrabold whitespace-nowrap cursor-pointer shrink-0 transition-colors"
                  @click="toggleCategoryPopover"
                >
                  {{ selectedCategoryLabel }}
                  <i class="ibi-chevron-down text-[10px] opacity-50" />
                </button>
                <Popover ref="categoryPopoverRef">
                  <div class="flex flex-col gap-1 p-3 min-w-[180px]">
                    <div class="text-[10px] font-extrabold uppercase tracking-wide text-on-surface px-1.5">
                      Category
                    </div>
                    <div class="flex flex-col gap-1">
                      <div class="flex flex-col">
                        <button
                          v-for="tab in electionTabs"
                          :key="tab.id"
                          class="category-popover__option hover:bg-surface-hover hover:text-on-surface p-1.5 rounded-md text-[13px] font-semibold text-on-surface text-left cursor-pointer transition-colors hover:bg-surface-100"
                          :class="{ 'category-popover__option--active': selectedTabId === tab.id }"
                          @click="
                            handleTabChange(tab.id);
                            categoryPopoverRef?.hide();
                          "
                        >
                          {{ tab.label }}
                        </button>
                      </div>
                    </div>
                  </div>
                </Popover>
              </template>
            </div>

            <!-- Table controls (filters + sort) -->
            <div class="ml-2 flex shrink-0 items-center gap-1.5">
              <div id="grid-compact-controls" class="contents" />
              <div id="grid-sort-controls" class="contents" />
            </div>

            <!-- Map / List toggle -->
            <div v-if="isTabsLoading" class="ml-auto shrink-0">
              <Skeleton width="5.5rem" height="2.25rem" borderRadius="8px" />
            </div>
            <div
              v-else
              class="ml-auto inline-flex shrink-0 items-center overflow-hidden rounded-lg border border-[--p-content-border-color] divide-x divide-[--p-content-border-color] h-9 xl:h-10"
            >
              <button
                v-for="opt in [
                  { value: 'dashboard' as const, label: t('map') },
                  { value: 'grid' as const, label: t('list') },
                ]"
                :key="opt.value"
                class="flex items-center justify-center px-2 sm:px-3 xl:px-4 h-full text-xs xl:text-sm font-extrabold cursor-pointer transition-colors"
                :class="
                  currentViewMode === opt.value
                    ? 'bg-election-brand text-on-election-brand'
                    : 'text-on-surface hover:bg-surface-hover hover:text-on-surface'
                "
                @click="handleViewModeChange(opt.value)"
              >
                {{ opt.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- End of hub-sticky-header -->

    <!-- Main Content \u2014 min-h-screen prevents collapse oscillation on short pages (mobile map) -->
    <main class="mx-auto mt-2 max-w-[1300px] px-3 sm:px-8 lg:px-12 xl:px-16">
      <!-- Open-account / $10 promo for visitors. Self-gates on a sessionStorage
           dismiss flag; renders nothing for authenticated users. -->
      <OpenAccountBanner v-if="!userState.authenticated && isUsDomain" class="mt-[16px]" />
      <div class="flex flex-col gap-4 lg:gap-6 mt-2">
        <!-- Hero Header Section (shared across views) -->
        <div
          v-if="currentViewMode === 'dashboard'"
          class="flex flex-col gap-3 sm:gap-6 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <p class="!font-black !leading-[1.0] tracking-[-0.02em] !text-[22px] sm:!text-[32px] md:!text-[48px]">
              <span v-if="currentTab !== 'All By State'">{{ currentTab }} Elections</span>
              <span v-else>{{ currentTab }}</span>
            </p>
            <p
              class="text-on-surface mt-1 sm:mt-2 max-w-xl text-sm sm:text-base md:text-[20px] font-normal leading-relaxed"
            >
              {{ t('predictTheWinners', { race: currentTab.toLowerCase() }) }}
            </p>
          </div>

          <!-- Open Interest Card (Desktop, dashboard only) -->
          <div v-if="currentViewMode === 'dashboard'" class="!visible hidden md:flex gap-4">
            <div
              class="border-[--p-content-border-color] bg-surface flex min-w-[200px] flex-col rounded-2xl border px-6 py-5"
            >
              <div class="text-on-surface mb-2 text-[11px] font-black uppercase tracking-widest">Open Interest</div>
              <Skeleton v-if="showOpenInterestSkeleton" height="2rem" width="5rem" />
              <div v-else class="text-on-surface text-[28px] font-black leading-tight">
                {{ formatOI(categoryOpenInterest) }}
              </div>
            </div>
          </div>
        </div>

        <!-- ===== DASHBOARD VIEW ===== -->
        <template v-if="currentViewMode === 'dashboard'">
          <!-- State Win Projection Bar (API-driven from partyAggregates) -->
          <div class="w-full space-y-2">
            <!-- Header: fixed height container to prevent reflow on hover -->
            <div class="relative min-h-[40px] lg:min-h-[56px]">
              <!-- Hover state: band description (overlays default) -->
              <div
                v-if="hoveredBandIndex != null"
                class="absolute inset-0 flex items-end"
                :class="{
                  'justify-start': confidenceBands[hoveredBandIndex].party === 'Democrat',
                  'justify-center': confidenceBands[hoveredBandIndex].party === 'Other',
                  'justify-end': confidenceBands[hoveredBandIndex].party === 'Republican',
                }"
              >
                <div class="flex items-baseline gap-2">
                  <span
                    class="text-[32px] lg:text-[44px] !font-black leading-none tracking-tight"
                    :class="{
                      'text-democrat': confidenceBands[hoveredBandIndex].party === 'Democrat',
                      'text-republican': confidenceBands[hoveredBandIndex].party === 'Republican',
                      'text-surface-500': confidenceBands[hoveredBandIndex].party === 'Other',
                    }"
                    >{{ confidenceBands[hoveredBandIndex].count }}</span
                  >
                  <span class="text-surface-500 text-[13px] lg:text-[15px] font-bold uppercase tracking-widest">{{
                    getBandDescription(confidenceBands[hoveredBandIndex])
                  }}</span>
                </div>
              </div>

              <!-- Default state -->
              <div
                class="flex flex-col transition-opacity duration-200"
                :class="hoveredBandIndex != null ? 'opacity-0' : 'opacity-100'"
              >
                <!-- Center info on mobile: own row above counts -->
                <div
                  v-if="!showMapSkeleton"
                  class="!visible text-surface-500 mb-1 whitespace-nowrap text-center text-[11px] font-bold uppercase tracking-widest md:hidden"
                >
                  <template v-if="showNotAvailable"> {{ notAvailableSeats }} not active | </template>
                  Total: {{ totalSeatsForCurrentTab }}
                </div>
                <!-- DEM count | center info (desktop) | REP count -->
                <div class="flex items-end justify-between">
                  <div class="flex items-baseline gap-2">
                    <Skeleton v-if="showMapSkeleton" width="2.5rem" height="29px" />
                    <span
                      v-else
                      class="text-democrat text-[32px] lg:text-[44px] !font-black leading-none tracking-tight"
                      >{{ partyAggregates.dem }}</span
                    >
                    <span class="text-surface-500 text-[13px] lg:text-[16px] font-bold uppercase tracking-widest">{{
                      t('democrat')
                    }}</span>
                  </div>
                  <div
                    v-if="!showMapSkeleton"
                    class="text-surface-500 hidden !visible whitespace-nowrap text-center text-[13px] lg:text-[15px] font-bold uppercase tracking-widest md:block"
                  >
                    <template v-if="showNotAvailable"> {{ notAvailableSeats }} not active | </template>
                    Total: {{ totalSeatsForCurrentTab }}
                  </div>
                  <div class="flex items-baseline gap-2">
                    <span class="text-surface-500 text-[13px] lg:text-[16px] font-bold uppercase tracking-widest">{{
                      t('republican')
                    }}</span>
                    <Skeleton v-if="showMapSkeleton" width="2.5rem" height="29px" />
                    <span
                      v-else
                      class="text-republican text-[32px] lg:text-[44px] !font-black leading-none tracking-tight"
                      >{{ partyAggregates.gop }}</span
                    >
                  </div>
                </div>
              </div>
            </div>

            <template v-if="showMapSkeleton">
              <Skeleton height="1.25rem" border-radius="9999px" />
            </template>
            <div
              v-else
              ref="progressBarEl"
              class="relative flex h-5 lg:h-7 w-full overflow-hidden rounded-full"
              @mouseleave="onBandMouseLeave"
            >
              <div
                v-for="(band, idx) in confidenceBands"
                :key="`${band.party}-${band.intensity}`"
                class="h-full cursor-pointer transition-all duration-300 ease-out"
                :class="[
                  idx === 0 && 'rounded-l-full',
                  idx === confidenceBands.length - 1 && 'rounded-r-full',
                  idx > 0 && 'border-l-2 border-white/30',
                  hoveredBandIndex != null && hoveredBandIndex !== idx && 'opacity-30',
                ]"
                :style="{
                  width: `${(band.count / partyAggregates.total) * 100}%`,
                  backgroundColor: getBandColor(band),
                }"
                @touchstart.passive="onBandTouchStart"
                @mouseenter="onBandMouseEnter(idx)"
                @click="onBandClick(idx)"
              />
            </div>
          </div>

          <!-- US Map Section -->
          <div class="flex flex-col gap-6">
            <div class="group relative">
              <section
                class="border-[--p-content-border-color] bg-surface overflow-hidden rounded-2xl lg:rounded-3xl border p-4 md:p-8 lg:p-10 xl:p-12"
              >
                <div v-if="!isMapZoomed" class="mb-2">
                  <div class="flex items-start justify-between">
                    <div>
                      <p class="text-on-surface text-[12px] lg:text-[14px] font-medium uppercase tracking-[0.08em]">
                        {{ currentTab === 'All By State' || currentTab === 'Governor' ? 'Gubernatorial' : currentTab }}
                        Contract Price (&cent;) &mdash; Select a state to view candidates
                      </p>
                    </div>
                  </div>
                </div>
                <div class="mx-auto mt-6">
                  <ElectionMap
                    :state-fills="stateFills"
                    :is-tab-switching="isTabSwitching"
                    :tracking-office="currentTab === 'All By State' ? 'governor' : currentTab.toLowerCase()"
                    tracking-race-type="general"
                    @select-state="handleMapSelectState"
                    @update:zoomed="isMapZoomed = $event"
                  />

                  <!-- Map Legend -->
                  <!-- When zoomed, floats over bottom of map via negative margin but stays in flow to preserve space -->
                  <div
                    class="relative z-10 mt-4 flex flex-col items-center gap-2 px-2 pt-4 transition-all duration-200"
                    :class="isMapZoomed ? '-mt-14' : ''"
                  >
                    <span
                      v-if="!isMapZoomed"
                      class="text-on-surface text-[11px] lg:text-[13px] font-bold uppercase tracking-widest"
                      >{{ t('winProbability') }}</span
                    >
                    <div class="flex w-full items-center justify-center gap-0.5 sm:gap-1 md:gap-1.5">
                      <span
                        v-if="!isMapZoomed"
                        class="!visible text-on-surface hidden shrink-0 font-bold uppercase tracking-wider sm:inline sm:text-[11px] lg:text-[13px]"
                        >{{ t('dem') }}</span
                      >
                      <div
                        v-for="(swatch, idx) in LEGEND_SWATCHES"
                        :key="idx"
                        class="legend-swatch flex h-6 min-w-0 flex-1 items-center justify-center rounded-sm text-[9px] font-bold text-white sm:text-[10px] md:h-7 md:max-w-11 md:text-[11px] lg:h-8 lg:max-w-14 lg:text-[13px]"
                        :style="{ backgroundColor: `var(${swatch.color})` }"
                      >
                        {{ swatch.label }}
                      </div>
                      <span
                        v-if="!isMapZoomed"
                        class="!visible text-on-surface hidden shrink-0 font-bold uppercase tracking-wider sm:inline sm:text-[11px] lg:text-[13px]"
                        >{{ t('rep') }}</span
                      >
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <!-- Quick Stats Grid - Mobile Only -->
            <div class="!visible grid grid-cols-1 gap-4 sm:grid-cols-3 md:hidden">
              <div class="border-[--p-content-border-color] bg-surface rounded-2xl border p-4 shadow-sm">
                <div class="text-surface-400 mb-1 text-[10px] font-black uppercase">{{ t('openInterest') }}</div>
                <Skeleton v-if="showOpenInterestSkeleton" height="1.5rem" width="4rem" />
                <div v-else class="text-on-surface text-lg font-black">{{ formatOI(categoryOpenInterest) }}</div>
              </div>
            </div>
          </div>
        </template>

        <!-- ===== ELECTION GRID VIEW ===== -->
        <template v-else>
          <!-- Election Grid - Now API-driven, no longer needs contracts prop. FTAPI-231's
               balance-of-power products are merged into its table rows internally. -->
          <ElectionGrid
            :election-type="currentTab"
            :view-mode="gridLayoutMode"
            :race-type-params="gridRaceTypeParams"
            @select-state="handleGridSelectState"
            @select-candidate="handleGridSelectCandidate"
            @update:view-mode="gridLayoutMode = $event"
            @share="handleGridShare"
          />
        </template>
      </div>
    </main>

    <!-- Details Panel (Drawer on mobile, Dialog on desktop) -->
    <ElectionDetailsPanel
      v-model:visible="detailsDrawerVisible"
      :products="selectedProducts"
      :initial-product-conid="initialProductConid"
      :initial-candidate-conid="initialCandidateConid"
      :party="selectedParty"
      :color="selectedColor"
      @share="handleShare($event)"
      @toggle-watchlist="handleToggleWatchlist"
      @show-grid="handleShowGrid"
    />

    <!-- Share Modal -->
    <ShareBetSlipDialog
      v-if="shareCandidateConid || shareProductConid || selectedProducts.length > 0"
      v-model:visible="shareModalVisible"
      :conid="shareCandidateConid"
      :product-conid="shareCandidateConid ? undefined : shareProductConid || selectedProducts[0]?.conid"
      @success="
        shareModalVisible = false;
        shareCandidateConid = undefined;
        shareProductConid = undefined;
      "
    />

    <!-- BetSlip Drawer (opened via deep links) -->
    <BetSlipDrawer v-model:visible="betSlipVisible" :conid="betSlipConid" :product-conid="betSlipProductConid" />
  </div>
</template>

<style scoped>
.legend-swatch {
  text-shadow:
    -1px -1px 0 rgb(0 0 0 / 60%),
    1px -1px 0 rgb(0 0 0 / 60%),
    -1px 1px 0 rgb(0 0 0 / 60%),
    1px 1px 0 rgb(0 0 0 / 60%);
}

/* \u2500\u2500 Sticky Header (needs CSS var for top) \u2500\u2500 */

.hub-sticky-header {
  top: var(--padding-top, 0);
  background: var(--p-surface-low);
}

/* \u2500\u2500 PrimeVue deep overrides (cannot be Tailwind) \u2500\u2500 */

.hub-sticky-header :deep(.p-tabs) {
  padding-top: 0;
  padding-bottom: 0;
}

/* \u2500\u2500 Active bar under tabs (needs !important for PrimeVue override) \u2500\u2500 */

.hub-active-bar {
  background: var(--color-election-brand) !important;
  height: 3px;
}

@media (width >= 1024px) {
  .hub-active-bar {
    height: 4px;
  }
}

/* \u2500\u2500 Category popover active state (needs color-mix) \u2500\u2500 */

.category-popover__option--active {
  color: var(--color-election-brand);
  background: color-mix(in srgb, var(--color-election-brand) 8%, transparent);
}
</style>
<!-- last modified by AI tools on 2026-10-09 -->
